import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { StarRating } from "@/components/StarRating";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/api";
import { toast } from "sonner";

const DRIVER_TAGS = ["punctual", "friendly", "safe_driving"];
const PASSENGER_TAGS = ["punctual", "friendly", "good_communication"];
const PARCEL_TAGS = ["punctual", "good_communication", "parcel_care"];

export const ReviewModal = ({ booking, open, onClose, onSubmitted }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ratingError, setRatingError] = useState(false);

  useEffect(() => {
    if (open) {
      setRating(0);
      setComment("");
      setTags([]);
      setRatingError(false);
    }
  }, [booking?.id, open]);

  const isRide = booking?.type === "ride";
  const revieweeIsDriver = user?.id === booking?.requester_id;
  const roleKey = revieweeIsDriver ? "review.roleDriver" : isRide ? "review.rolePassenger" : "review.roleSender";
  const tagOptions = isRide ? (revieweeIsDriver ? DRIVER_TAGS : PASSENGER_TAGS) : PARCEL_TAGS;

  const toggleTag = (tag) => setTags((prev) => (prev.includes(tag) ? prev.filter((x) => x !== tag) : [...prev, tag]));

  const submit = async () => {
    if (rating === 0) {
      setRatingError(true);
      return;
    }
    setLoading(true);
    try {
      await api.post("/reviews", { booking_id: booking.id, rating, comment, tags });
      toast.success(t("review.thanks"));
      onSubmitted?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="rounded-2xl" data-testid="review-modal">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2 flex-wrap">
            {t("review.title")} — {booking?.other_user?.name}
            <Badge variant="secondary" data-testid="review-role-badge" className="text-xs">{t(roleKey)}</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div>
            <p className="text-sm font-medium mb-2">{t("review.rating")}</p>
            <StarRating value={rating} onChange={(v) => { setRating(v); setRatingError(false); }} size={28} testid="review-stars" />
            {ratingError && <p className="text-sm text-destructive mt-1.5" data-testid="review-rating-error">{t("review.selectRating")}</p>}
          </div>
          <div>
            <p className="text-sm font-medium mb-2">{t("review.tags")}</p>
            <div className="flex flex-wrap gap-2">
              {tagOptions.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  data-testid={`review-tag-${tag}`}
                  onClick={() => toggleTag(tag)}
                  className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-colors duration-200 ${tags.includes(tag) ? "bg-primary text-white" : "bg-blue-50 text-blue-700 hover:bg-blue-100"}`}
                >
                  {t(`tagLabels.${tag}`)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-sm font-medium mb-2">{t("review.comment")}</p>
            <Textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={3} className="rounded-xl" />
          </div>
          <Button data-testid="review-submit" onClick={submit} disabled={loading} className="w-full rounded-full bg-accent hover:bg-accent/90 text-white">
            {t("review.submit")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
